import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth0 } from '../../hooks/useAuth0';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useVoice } from '../../hooks/useVoice';
import ConnectEpicButton from '../ConnectEpicButton';
import SourcesPanel from '../SourcesPanel';
import AIDisclosureModal, { hasAcceptedAIDisclosure, recordAIDisclosureAcceptance } from './AIDisclosureModal';

// ── Constants ────────────────────────────────────────────────────────────────
const FREE_LIMIT = 3;

const PREMIUM_ENABLED = process.env.REACT_APP_ENABLE_PREMIUM === 'true';
const STORAGE_KEY = 'copilot_session_count';

// ── Context Definitions ──────────────────────────────────────────────────────
const CONTEXTS = [
  {
    id: 'general',
    icon: '🧬',
    label: 'CKD',
    tooltip: 'General CKD Questions',
    color: '#2d9de8',
    systemNote: 'I am now focused on answering general CKD questions — causes, symptoms, diagnosis, and treatment options.',
    systemPrompt: 'You are Care Copilot, an AI health companion specialized in chronic kidney disease (CKD). Answer questions about diabetic nephropathy, hypertensive nephrosclerosis, glomerulonephritis, and polycystic kidney disease — symptoms, diagnosis, CKD stage, and treatment options — in plain, compassionate language suitable for patients and caregivers.',
  },
  {
    id: 'labs',
    icon: '🧪',
    label: 'Lab Results',
    tooltip: 'Discuss My Lab Results',
    color: '#0ab5a0',
    systemNote: 'I am now in Lab Results mode. Share your lab values and I will explain what they mean for your CKD.',
    systemPrompt: 'You are Care Copilot in Lab Results mode. Help patients understand their CKD-related lab values — eGFR, UACR, potassium, PTH, hemoglobin, phosphorus, and others. Explain values in plain English, note what is normal vs. abnormal, and explain clinical significance without giving medical advice.',
  },
  {
    id: 'diagnosis',
    icon: '🩺',
    label: 'My Diagnosis',
    tooltip: "Discuss Doctor's Diagnosis",
    color: '#5b3fa6',
    systemNote: "I am now in Diagnosis Discussion mode. Share what your doctor told you and I'll help you understand it.",
    systemPrompt: "You are Care Copilot in Diagnosis Discussion mode. Help patients understand their CKD diagnosis — etiology (diabetic, hypertensive, glomerulonephritis, PKD, or other), CKD stage, complications, and what the diagnosis means for their treatment path. Explain medical terminology in plain language and help patients formulate questions for their doctor.",
  },
  {
    id: 'trials',
    icon: '🎯',
    label: 'Clinical Trials',
    tooltip: 'Understand Clinical Trials',
    color: '#e8a020',
    systemNote: 'I am now in Clinical Trials mode. Ask me anything about finding, understanding, or enrolling in CKD trials.',
    systemPrompt: 'You are Care Copilot in Clinical Trials mode. Help patients understand clinical trials for CKD — how to find them, eligibility criteria, what participation involves, phases of trials, informed consent, and how to talk to their care team about trial options.',
  },
  {
    id: 'records',
    icon: '📄',
    label: 'Medical Records',
    tooltip: 'Understand My Medical Records',
    color: '#e05c3a',
    systemNote: 'I am now in Medical Records mode. Share details from your records and I will help you understand them.',
    systemPrompt: 'You are Care Copilot in Medical Records mode. Help patients interpret their medical records — kidney biopsy reports, imaging findings, discharge summaries, and specialist letters related to CKD. Explain medical terminology, test results, and clinical findings in plain language patients can understand.',
  },
  {
    id: 'medications',
    icon: '💊',
    label: 'Medications',
    tooltip: 'Medications & Treatment',
    color: '#059669',
    systemNote: 'I am now in Medications mode. Ask me about your CKD medications, side effects, or treatment options.',
    systemPrompt: 'You are Care Copilot in Medications mode. Help patients understand CKD medications — ACE inhibitors, ARBs, SGLT2 inhibitors, finerenone, phosphate binders, tolvaptan, and others. Explain mechanism of action, dosing, common side effects, and what to monitor in plain language. Always remind patients to consult their physician before making any medication changes.',
  },
  {
    id: 'doctorvisit',
    icon: '📋',
    label: 'Doctor Visit',
    tooltip: 'Prepare for Doctor Visit',
    color: '#1a5fa8',
    systemNote: 'I am now in Doctor Visit Prep mode. Tell me about your upcoming appointment and I will help you prepare.',
    systemPrompt: 'You are Care Copilot in Doctor Visit Preparation mode. Help CKD patients prepare for appointments with nephrologists or other specialists. Generate relevant questions based on their diagnosis and concerns, explain what tests might be ordered, and help them organize their symptoms and history to communicate effectively with their care team.',
  },
  {
    id: 'type',
    icon: '🔬',
    label: 'My Cause',
    tooltip: 'Understand Your CKD Cause',
    color: '#0B2545',
    systemNote: 'I am now in CKD Cause mode. Tell me your cause and I will explain everything specific to your situation.',
    systemPrompt: 'You are Care Copilot in CKD Cause Explanation mode. Help patients deeply understand their specific CKD etiology — diabetic nephropathy, hypertensive nephrosclerosis, glomerulonephritis, polycystic kidney disease, or other/unknown causes. Cover typical complications, prognosis, available treatments, genetic implications for family members where relevant (e.g. PKD), and what questions to ask their specialist.',
  },

  {
  id: 'profile',
  icon: '📝',
  label: 'My Profile',
  tooltip: 'Complete My Profile',
  color: '#f59e0b',
  systemNote: "I am now in Profile Assistant mode. Let's get your profile filled in!",
  systemPrompt: null,
},
];

// ── Design tokens ────────────────────────────────────────────────────────────
const C = {
  navy:  '#0D2B45',
  blue:  '#1a5fa8',
  sky:   '#2d9de8',
  teal:  '#0ab5a0',
  white: '#ffffff',
  dark:  '#0a1628',
  slate: '#64748b',
  off:   '#f7f9fc',
};

const fonts = {
  display: "'Playfair Display', Georgia, serif",
  body:    "'DM Sans', system-ui, sans-serif",
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=DM+Sans:wght@300;400;500;600;700&display=swap');
  @keyframes fadeUp   { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
  @keyframes bounce   { 0%,60%,100%{transform:translateY(0)} 30%{transform:translateY(-6px)} }
  @keyframes pulse    { 0%,100%{opacity:.6} 50%{opacity:1} }
  @keyframes slideIn  { from{opacity:0;transform:translateX(12px)} to{opacity:1;transform:translateX(0)} }
  @keyframes contextPop { from{transform:scale(.95);opacity:0} to{transform:scale(1);opacity:1} }
  @keyframes ccMicPulse { 0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,0.5);} 50%{box-shadow:0 0 0 8px rgba(239,68,68,0);} }

  .cc-msg     { animation: fadeUp .4s ease both; }
  .cc-ctx-item { animation: contextPop .3s ease both; }

  .cc-ctx-btn {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 5px;
    width: 100%;
    padding: 10px 6px;
    border-radius: 12px;
    border: 1.5px solid transparent;
    background: rgba(255,255,255,.06);
    cursor: pointer;
    transition: all .2s ease;
    font-family: 'DM Sans', system-ui, sans-serif;
  }
  .cc-ctx-btn:hover {
    background: rgba(255,255,255,.12);
    transform: translateY(-2px);
  }
  .cc-ctx-btn.active {
    background: rgba(255,255,255,.12);
    transform: translateY(-1px);
  }
  .cc-ctx-icon { font-size: 20px; line-height: 1; }
  .cc-ctx-label {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: .04em;
    text-align: center;
    line-height: 1.2;
    color: rgba(255,255,255,.7);
    max-width: 60px;
    word-wrap: break-word;
  }
  .cc-ctx-btn.active .cc-ctx-label { color: #fff; }

  .cc-ctx-sidebar {
    width: 76px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 16px 8px;
    background: rgba(0,0,0,.15);
    border-right: 1px solid rgba(255,255,255,.06);
    overflow-y: auto;
  }

  /* On tablets and wider, there's plenty of room — widen the sidebar and
     labels so multi-word context names ("Lab Results", "My Diagnosis",
     "Doctor Visit") don't wrap onto two cramped lines. This was a real,
     visually-confirmed crowding issue on iPad width (820px), likely
     contributing to App Store Guideline 4's "crowded interface" feedback. */
  @media (min-width: 600px) {
    .cc-ctx-sidebar { width: 108px; padding: 16px 10px; }
    .cc-ctx-label { max-width: 92px; font-size: 11px; }
    .cc-ctx-icon { font-size: 22px; }
  }

  .cc-tooltip {
    position: absolute;
    left: calc(100% + 10px);
    top: 50%;
    transform: translateY(-50%);
    background: rgba(10,22,40,.95);
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
    padding: 6px 12px;
    border-radius: 8px;
    pointer-events: none;
    opacity: 0;
    transition: opacity .2s;
    z-index: 100;
    border: 1px solid rgba(255,255,255,.1);
  }
  .cc-ctx-btn:hover .cc-tooltip { opacity: 1; }

  .cc-input {
    flex: 1;
    padding: 12px 16px;
    border-radius: 12px;
    background: rgba(255,255,255,.08);
    border: 1px solid rgba(255,255,255,.15);
    color: white;
    font-size: 14px;
    font-family: 'DM Sans', system-ui, sans-serif;
    outline: none;
    resize: none;
    line-height: 1.5;
    transition: border-color .2s;
  }
  .cc-input:focus { border-color: rgba(45,157,232,.5); }
  .cc-input::placeholder { color: rgba(255,255,255,.35); }

  .cc-send {
    padding: 12px 16px;
    border-radius: 12px;
    border: none;
    cursor: pointer;
    font-size: 18px;
    line-height: 1;
    transition: all .2s;
  }
  .cc-send:not(:disabled):hover { transform: translateY(-2px); }

  .cc-mic {
    padding: 12px 14px;
    border-radius: 12px;
    cursor: pointer;
    font-size: 18px;
    line-height: 1;
    transition: all .2s;
    flex-shrink: 0;
  }
  .cc-mic:not(:disabled):hover { transform: translateY(-2px); }

  .cc-note-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    animation: slideIn .3s ease both;
  }

  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,.15); border-radius: 4px; }
`;

// ── Subcomponents ────────────────────────────────────────────────────────────
function LoadingDots() {
  return (
    <div style={{ display:'flex', gap:4, alignItems:'center', padding:'4px 0' }}>
      <style>{`@keyframes b{0%,60%,100%{transform:translateY(0)}30%{transform:translateY(-6px)}}`}</style>
      {[0,.15,.3].map((d,i) => (
        <div key={i} style={{ width:8, height:8, borderRadius:'50%', background:'#1A5EA8', animation:`b 1s infinite ${d}s` }}/>
      ))}
    </div>
  );
}

function renderText(text) {
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br>');
}

function Message({ msg, contexts }) {
  const isUser = msg.role === 'user';
  const isNote = msg.role === 'note';

  if (isNote) {
    const ctx = contexts.find(c => c.id === msg.contextId);
    return (
      <div style={{ display:'flex', justifyContent:'center', margin:'8px 0' }}>
        <span className="cc-note-badge" style={{
          background: ctx ? `${ctx.color}22` : 'rgba(255,255,255,.08)',
          color: ctx ? ctx.color : 'rgba(255,255,255,.6)',
          border: `1px solid ${ctx ? ctx.color + '40' : 'rgba(255,255,255,.15)'}`,
        }}>
          {ctx && <span>{ctx.icon}</span>}
          <span>{msg.content}</span>
        </span>
      </div>
    );
  }

  return (
    <div className="cc-msg" style={{ display:'flex', gap:10, alignItems:'flex-start', flexDirection: isUser ? 'row-reverse' : 'row' }}>
      <div style={{
        width:34, height:34, borderRadius:'50%', flexShrink:0,
        display:'flex', alignItems:'center', justifyContent:'center', fontSize:16,
        background: isUser ? 'linear-gradient(135deg,#1A5EA8,#0B2545)' : 'rgba(255,255,255,.1)',
        border: isUser ? 'none' : '1px solid rgba(255,255,255,.15)',
      }}>
        {isUser ? '👤' : '🤝'}
      </div>
      <div style={{
        maxWidth:'78%', padding:'13px 17px',
        borderRadius: isUser ? '18px 4px 18px 18px' : '4px 18px 18px 18px',
        fontSize:14, lineHeight:1.75,
        background: isUser ? 'linear-gradient(135deg,#1A5EA8,#0B2545)' : 'rgba(255,255,255,.08)',
        color: isUser ? 'white' : 'rgba(255,255,255,.92)',
        border: isUser ? 'none' : '1px solid rgba(255,255,255,.1)',
        boxShadow: isUser ? '0 4px 20px rgba(102,126,234,.3)' : 'none',
      }} dangerouslySetInnerHTML={{ __html: renderText(msg.content) }}/>
    </div>
  );
}

// ── Context Sidebar ──────────────────────────────────────────────────────────
function ContextSidebar({ activeContextId, onSelect }) {
  return (
    <div className="cc-ctx-sidebar" style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      padding: '16px 8px',
      background: 'rgba(0,0,0,.15)',
      borderRight: '1px solid rgba(255,255,255,.06)',
      overflowY: 'auto',
    }}>
      <p style={{
        fontFamily: fonts.body,
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: '.1em',
        textTransform: 'uppercase',
        color: 'rgba(255,255,255,.3)',
        textAlign: 'center',
        margin: '0 0 8px',
      }}>Focus</p>

      {CONTEXTS.map((ctx, i) => (
        <button
          key={ctx.id}
          className={`cc-ctx-item cc-ctx-btn ${activeContextId === ctx.id ? 'active' : ''}`}
          style={{
            animationDelay: `${i * .04}s`,
            borderColor: activeContextId === ctx.id ? `${ctx.color}60` : 'transparent',
            boxShadow: activeContextId === ctx.id ? `0 0 12px ${ctx.color}30` : 'none',
          }}
          onClick={() => onSelect(ctx)}
          title={ctx.tooltip}
        >
          <span className="cc-ctx-icon">{ctx.icon}</span>
          <span className="cc-ctx-label" style={{ color: activeContextId === ctx.id ? ctx.color : undefined }}>
            {ctx.label}
          </span>
          {/* Active indicator dot */}
          {activeContextId === ctx.id && (
            <div style={{
              position: 'absolute',
              top: 6, right: 6,
              width: 5, height: 5,
              borderRadius: '50%',
              background: ctx.color,
              animation: 'pulse 2s infinite',
            }}/>
          )}
          <span className="cc-tooltip">{ctx.tooltip}</span>
        </button>
      ))}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
const CareCopilot = () => {
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();

  const [messages, setMessages] = useState([{
    id: 1, role: 'assistant',
    content: "Hi! I'm **Care Copilot**, your personal CKD AI companion.\n\nI can answer questions about your condition, explain clinical trials, help interpret lab results, prepare you for doctor visits, and much more.\n\nUse the **Focus panel** on the left to guide our conversation — or just ask me anything, by typing or tapping the mic to speak.",
  }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [freeCount, setFreeCount] = useState(0);
  const [tier, setTier] = useState('sonnet');
  const [showConsent, setShowConsent] = useState(false);
  const [consentGiven, setConsentGiven] = useState(null);
  const [showAIDisclosure, setShowAIDisclosure] = useState(false);
  const [pendingQuestion, setPendingQuestion] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [activeContext, setActiveContext] = useState(CONTEXTS[0]);
  const [muted, setMuted] = useState(false);
  const [showLabFollowUp, setShowLabFollowUp] = useState(false);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const [appHelperHistory, setAppHelperHistory] = useState([]);

  // handleSend is defined below via `const handleSend = ...`; handleTranscript
  // only executes later (after a user taps the mic and stops speaking), by which
  // point handleSend already exists in this closure — same pattern used in the
  // Care Copilot widget.
  const handleTranscript = useCallback((transcript) => {
    if (!transcript?.trim()) return;
    setInput(transcript);
    handleSend(transcript);
  }, []); // eslint-disable-line

  const { speak, stopSpeaking, startListening, stopListening, speaking, listening, supported } =
    useVoice({ muted, onTranscript: handleTranscript });

  useEffect(() => {
    if (!isAuthenticated) {
      loginWithRedirect({ appState: { returnTo: '/my-health/care-copilot' } });
      return;
    }
    const count = parseInt(sessionStorage.getItem(STORAGE_KEY) || '0');
    setFreeCount(count);
  }, [isAuthenticated]);

  useEffect(() => {
    // Scroll within chat area only — not the page
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [messages, loading]);

  const addMessage = useCallback((role, content, extra = {}) => {
    setMessages(prev => [...prev, { id: Date.now() + Math.random(), role, content, ...extra }]);
  }, []);

  const handleContextSelect = useCallback((ctx) => {
    setActiveContext(ctx);
    addMessage('note', ctx.systemNote, { contextId: ctx.id });
    // Auto-guide when Lab Results mode selected
    if (ctx.id === 'labs') {
      setTimeout(() => {
        addMessage('assistant', "You're now in **Lab Results mode** 🧪\n\nYou can:\n• **Upload** a PDF or image of your lab results using the upload button below\n• **Type** your values directly (e.g. \"My NT-proBNP is 1800 and troponin is 0.03\")\n\nI'll explain what your results mean in plain English.");
      }, 300);
    }
  }, [addMessage]);

  const getEndpoint = () => {
    // No subscription tiers — Sonnet for logged-in users, Haiku (rate-limited
    // to FREE_LIMIT per IP, see backend) for anonymous ones.
    return isAuthenticated ? '/v1/copilot/ask-sonnet' : '/v1/copilot/ask';
  };

  const handleSend = async (questionOverride) => {
    const question = questionOverride || input.trim();
    if (!question || loading) return;
    if (tier === 'free' && freeCount >= FREE_LIMIT) return;

    // First-use AI data disclosure gate (App Store Guideline
    // 5.1.1(i)/5.1.2(i)) — hold the message until the user has seen and
    // accepted the disclosure, then send it once they do.
    if (!hasAcceptedAIDisclosure()) {
      setPendingQuestion(question);
      setShowAIDisclosure(true);
      return;
    }

    setInput('');
    addMessage('user', question);
    setLoading(true);

    if (activeContext.id === 'profile') {
      try {
        const response = await api.post('/v1/app-helper/chat', {
          message: question,
          conversationHistory: appHelperHistory,
        });
        const data = response.data;
        setAppHelperHistory(data.updatedHistory);

        const lastTurn = data.updatedHistory[data.updatedHistory.length - 1];
        const displayText = Array.isArray(lastTurn.content)
          ? lastTurn.content.filter(b => b.type === 'text').map(b => b.text).join('\n')
          : lastTurn.content;

        const finalText = displayText || 'Let\'s continue — what would you like to share next?';
        addMessage('assistant', finalText);
        speak(finalText);
      } catch (err) {
        const errMsg = 'I ran into an issue saving that — let\'s try again.';
        addMessage('assistant', errMsg);
        speak(errMsg);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (tier === 'free') {
      const newCount = freeCount + 1;
      setFreeCount(newCount);
      sessionStorage.setItem(STORAGE_KEY, newCount.toString());
    }

    try {
      const response = await api.post(getEndpoint(), {
        question,
        systemPrompt: activeContext.systemPrompt,
        contextId: activeContext.id,
      });
      const data = response.data;

      if (data.limitReached) {
        const msg = PREMIUM_ENABLED
          ? "You've reached your **3 free questions**. Upgrade to **Care Copilot Premium** to continue with unlimited questions and lab result interpretation."
          : "You've reached the free question limit. **Please log in** to continue with unlimited questions.";
        addMessage('assistant', msg);
        speak(msg);
      } else {
        const answer = data.answer || 'I was unable to process your question. Please try again.';
        addMessage('assistant', answer);
        speak(answer);
      }
    } catch (err) {
      if (err.response?.status === 429) {
        const msg = PREMIUM_ENABLED
          ? "You've reached the free question limit. **Upgrade to Care Copilot Premium** for unlimited access!"
          : "You've reached the free question limit. **Please log in** for unlimited access.";
        addMessage('assistant', msg);
      } else {
        addMessage('assistant', 'I apologize, there was a connection error. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMicClick = () => {
    if (listening) stopListening(); else { stopSpeaking(); startListening(); }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const labUploadBlocked = PREMIUM_ENABLED ? tier === 'free' : !isAuthenticated;
    if (labUploadBlocked) {
      // Switch to lab context and notify
      handleContextSelect(CONTEXTS.find(c => c.id === 'labs'));
      const msg = PREMIUM_ENABLED
        ? '**Lab result interpretation** is a Care Copilot Premium feature. Upgrade to upload and interpret your lab results — NT-proBNP, free light chains, eGFR, troponin, and more.'
        : 'Please **log in** to upload and interpret your lab results — NT-proBNP, free light chains, eGFR, troponin, and more.';
      addMessage('assistant', msg);
      return;
    }
    if (consentGiven === null) {
      setUploadedFile(file);
      setShowConsent(true);
    } else {
      processFileUpload(file);
    }
  };

  const processFileUpload = async (file) => {
    // Auto-switch to lab context on file upload
    if (activeContext.id !== 'labs') {
      handleContextSelect(CONTEXTS.find(c => c.id === 'labs'));
    }
    setUploadedFile(file);
    addMessage('user', `📎 Uploaded: ${file.name}`);
    setLoading(true);
    addMessage('assistant', `🧪 Analyzing your lab results from **${file.name}**...\n\nThis may take 15–30 seconds.`);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('consentGiven', String(consentGiven ?? false));
      const response = await api.post('/v1/labs/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      // Remove the "analyzing" message
      setMessages(prev => prev.filter(m => !m.content.includes('Analyzing your lab results')));
      const interpretation = response.data?.interpretation || 'Lab results received. Your values have been stored securely.';
      const fullMsg = `📋 **Lab Result Interpretation**\n\n${interpretation}`;
      addMessage('assistant', fullMsg);
      speak(fullMsg);
      setTimeout(() => setShowLabFollowUp(true), 800);
    } catch (err) {
      setMessages(prev => prev.filter(m => !m.content.includes('Analyzing your lab results')));
      addMessage('assistant', '❌ Unable to process the lab results file. Please try again with a clear PDF or image, or type your values directly and I will interpret them.');
    } finally {
      setLoading(false);
      setUploadedFile(null);
    }
  };

  const isLimitReached = PREMIUM_ENABLED
    ? (tier === 'free' && freeCount >= FREE_LIMIT)
    : (!isAuthenticated && freeCount >= FREE_LIMIT);
  const remaining = Math.max(0, FREE_LIMIT - freeCount);

  return (
    <div style={{ minHeight:'100vh', background:`linear-gradient(135deg,${C.dark} 0%,${C.navy} 40%,#2d1b69 100%)`, fontFamily:fonts.body, display:'flex', flexDirection:'column' }}>
      <style>{STYLES}</style>

      {/* ── Header ── */}
      <div style={{
        background:'rgba(13,43,69,.8)', backdropFilter:'blur(20px)',
        borderBottom:'1px solid rgba(255,255,255,.08)',
        padding:'16px 24px',
        display:'flex', alignItems:'center', justifyContent:'space-between',
        flexShrink: 0,
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:14 }}>
          <div style={{
            width:46, height:46, borderRadius:13,
            background:`linear-gradient(135deg,${C.sky},${C.teal})`,
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:22, boxShadow:`0 4px 16px ${C.sky}44`,
          }}>🤝</div>
          <div>
            <h1 style={{ fontFamily:fonts.display, fontSize:22, fontWeight:700, color:'white', margin:0 }}>Care Copilot</h1>
            <p style={{ fontSize:11, color:'rgba(255,255,255,.45)', margin:0, display:'flex', alignItems:'center', gap:6 }}>
              <span style={{ width:6, height:6, borderRadius:'50%', background: listening ? '#ef4444' : speaking ? '#1A5EA8' : '#10b981', animation: (listening || speaking) ? 'ccMicPulse 1s infinite' : 'none' }} />
              {listening ? '🎤 Listening...' : speaking ? '🔊 Speaking...' : 'by NephroTrax · Your Personal Health AI'}
            </p>
          </div>
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          {/* Active context indicator */}
          <div style={{
            display:'flex', alignItems:'center', gap:7,
            padding:'6px 14px', borderRadius:999,
            background:`${activeContext.color}18`,
            border:`1px solid ${activeContext.color}35`,
          }}>
            <span style={{ fontSize:14 }}>{activeContext.icon}</span>
            <span style={{ fontSize:12, fontWeight:600, color:activeContext.color }}>{activeContext.tooltip}</span>
          </div>

          <SourcesPanel contextId={activeContext.id} />

          {PREMIUM_ENABLED && tier === 'free' && (
            <span style={{
              background:'rgba(102,126,234,.2)', border:'1px solid rgba(102,126,234,.4)',
              color:'#a78bfa', borderRadius:999, padding:'4px 12px',
              fontSize:11, fontWeight:700, letterSpacing:'.06em', textTransform:'uppercase',
            }}>
              Free — {remaining} left
            </span>
          )}
          {PREMIUM_ENABLED && tier === 'sonnet' && (
            <span style={{ background:'linear-gradient(135deg,#f59e0b,#d97706)', color:'white', borderRadius:999, padding:'4px 12px', fontSize:11, fontWeight:700, textTransform:'uppercase' }}>
              Premium Sonnet
            </span>
          )}

          <button onClick={() => { setMuted(m => !m); if (!muted) stopSpeaking(); }}
            style={{
              background: muted ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,.08)',
              border: muted ? '1px solid rgba(239,68,68,0.4)' : '1px solid rgba(255,255,255,.18)',
              borderRadius:8, color: muted ? '#fca5a5' : 'rgba(255,255,255,0.8)',
              padding:'7px 10px', cursor:'pointer', fontSize:16, lineHeight:1,
            }}>
            {muted ? '🔇' : '🔊'}
          </button>
          <button onClick={() => navigate(-1)} style={{
            background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.18)',
            borderRadius:8, color:'white', padding:'7px 16px',
            cursor:'pointer', fontSize:13, fontFamily:fonts.body,
          }}>
            ⊡ Minimize
          </button>
          <button onClick={() => navigate('/dashboard')} style={{
            background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.18)',
            borderRadius:8, color:'white', padding:'7px 16px',
            cursor:'pointer', fontSize:13, fontFamily:fonts.body,
          }}>
            ← Dashboard
          </button>
        </div>
      </div>

      {/* ── Main Layout ── */}
      <div style={{ flex:1, display:'flex', overflow:'hidden', maxHeight:'calc(100vh - 80px)' }}>

        {/* ── Context Sidebar ── */}
        <ContextSidebar activeContextId={activeContext.id} onSelect={handleContextSelect} />

        {/* ── Chat Area ── */}
        <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>

          {/* Messages */}
          <div style={{
            flex:1, overflowY:'auto', padding:'24px 28px',
            display:'flex', flexDirection:'column', gap:14,
          }}>
            {messages.map(msg => (
              <Message key={msg.id} msg={msg} contexts={CONTEXTS} />
            ))}
            {loading && (
              <div className="cc-msg" style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                <div style={{ width:34, height:34, borderRadius:'50%', background:'rgba(255,255,255,.1)', border:'1px solid rgba(255,255,255,.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16 }}>🤝</div>
                <div style={{ padding:'13px 17px', borderRadius:'4px 18px 18px 18px', background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.1)' }}>
                  <LoadingDots />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div style={{
            padding:'14px 20px 18px',
            borderTop:'1px solid rgba(255,255,255,.07)',
            background:'rgba(0,0,0,.12)',
            flexShrink: 0,
          }}>
            {/* Context bar above input */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
              <span style={{ fontSize:12, color:'rgba(255,255,255,.35)' }}>
                {PREMIUM_ENABLED
                  ? (tier === 'free'
                      ? isLimitReached ? '🔒 Upgrade for unlimited questions' : `${remaining} free question${remaining !== 1 ? 's' : ''} remaining`
                      : '✨ Unlimited questions')
                  : (isAuthenticated
                      ? '✨ Unlimited questions'
                      : isLimitReached ? '🔒 Log in for unlimited questions' : `${remaining} free question${remaining !== 1 ? 's' : ''} remaining`)}
              </span>
              <span style={{
                fontSize:11, fontWeight:700, letterSpacing:'.06em', textTransform:'uppercase',
                color: activeContext.color,
              }}>
                {activeContext.icon} {activeContext.label} Mode
              </span>
            </div>

            {/* Connect Epic health records — shows only in Profile mode */}
            {activeContext.id === 'profile' && (
              <div style={{ padding: '0 0 12px' }}>
                <ConnectEpicButton />
              </div>
            )}

            {/* Lab follow-up prompt buttons */}
            {showLabFollowUp && (
              <div style={{ padding:'0 0 12px', display:'flex', gap:8, flexWrap:'wrap' }}>
                {[
                  "What do my lab results mean for my CKD?",
                  "Which values are abnormal?",
                  "What should I ask my doctor about these results?",
                  "How do these compare to normal ranges?",
                ].map((prompt, i) => (
                  <button key={i} onClick={() => {
                    setShowLabFollowUp(false);
                    handleSend(prompt);
                  }} style={{
                    background:'rgba(10,181,160,0.15)',
                    border:'1px solid rgba(10,181,160,0.4)',
                    borderRadius:20, padding:'6px 14px',
                    fontSize:12, fontWeight:600,
                    color:'#0ab5a0', cursor:'pointer',
                    fontFamily:"'DM Sans',system-ui,sans-serif",
                    transition:'all 0.2s',
                  }}>
                    {prompt}
                  </button>
                ))}
              </div>
            )}

            {/* Lab upload bar — shows above input when in Labs mode */}
            {activeContext.id === 'labs' && (
              <div style={{
                display:'flex', alignItems:'center', justifyContent:'space-between',
                background:`${C.teal}15`, border:`1px solid ${C.teal}40`,
                borderRadius:12, padding:'10px 14px', marginBottom:10,
              }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontSize:18 }}>🧪</span>
                  <div>
                    <div style={{ fontSize:13, fontWeight:600, color:C.teal }}>Upload Lab Results</div>
                    <div style={{ fontSize:11, color:'rgba(255,255,255,.5)' }}>PDF or image — I'll interpret your values</div>
                  </div>
                </div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    padding:'8px 16px', borderRadius:8, cursor:'pointer',
                    fontSize:12, fontWeight:700, lineHeight:1,
                    background:`linear-gradient(135deg,${C.teal},${C.sky})`,
                    border:'none', color:'white', fontFamily:"'DM Sans',system-ui,sans-serif",
                    transition:'all .2s',
                  }}
                >Upload File →</button>
                <input ref={fileInputRef} type="file" accept=".pdf,image/*" style={{ display:'none' }} onChange={handleFileUpload} />
              </div>
            )}

            <div style={{ display:'flex', gap:9, alignItems:'flex-end' }}>
              {supported.stt && (
                <button
                  className="cc-mic"
                  onClick={handleMicClick}
                  disabled={isLimitReached || loading}
                  title={listening ? 'Stop listening' : 'Tap to speak'}
                  style={{
                    background: listening ? 'rgba(239,68,68,0.25)' : 'rgba(255,255,255,.08)',
                    border: listening ? '1px solid rgba(239,68,68,0.5)' : '1px solid rgba(255,255,255,.18)',
                    color: listening ? '#fca5a5' : 'rgba(255,255,255,.8)',
                    cursor: (isLimitReached || loading) ? 'not-allowed' : 'pointer',
                    animation: listening ? 'ccMicPulse 1s infinite' : 'none',
                    opacity: (isLimitReached || loading) ? .45 : 1,
                  }}
                >
                  {listening ? '⏹' : '🎤'}
                </button>
              )}

              <textarea
                className="cc-input"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyPress={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                placeholder={listening ? 'Listening... speak now' : isLimitReached ? (PREMIUM_ENABLED ? 'Upgrade to ask more questions...' : 'Log in to ask more questions...') : `Ask about ${activeContext.tooltip.toLowerCase()}, or tap 🎤 to speak...`}
                disabled={isLimitReached || loading}
                rows={1}
                style={{ opacity: isLimitReached ? .45 : 1, cursor: isLimitReached ? 'not-allowed' : 'text' }}
              />

              <button
                className="cc-send"
                onClick={() => handleSend()}
                disabled={isLimitReached || loading || !input.trim()}
                style={{
                  background: (isLimitReached || loading || !input.trim())
                    ? 'rgba(255,255,255,.08)'
                    : `linear-gradient(135deg,${activeContext.color},${activeContext.color}cc)`,
                  color: (isLimitReached || loading || !input.trim()) ? 'rgba(255,255,255,.25)' : 'white',
                  cursor: (isLimitReached || loading || !input.trim()) ? 'not-allowed' : 'pointer',
                  boxShadow: (isLimitReached || loading || !input.trim()) ? 'none' : `0 4px 16px ${activeContext.color}44`,
                }}
              >↑</button>
            </div>

            <p style={{ fontSize:11, color:'rgba(255,255,255,.25)', marginTop:8, textAlign:'center' }}>
              ⚕️ For educational purposes only. Always consult your physician for medical advice.
            </p>
          </div>
        </div>

        {/* ── Right Panel — Upgrade + What I Can Help With ── */}
        <div style={{
          width:260, flexShrink:0,
          display:'flex', flexDirection:'column', gap:14,
          padding:'16px 14px',
          background:'rgba(0,0,0,.1)',
          borderLeft:'1px solid rgba(255,255,255,.06)',
          overflowY:'auto',
        }}>

          {/* Upgrade card */}
          {PREMIUM_ENABLED && tier === 'free' && (
            <div style={{
              background:'linear-gradient(135deg,rgba(102,126,234,.18),rgba(118,75,162,.18))',
              border:'1px solid rgba(102,126,234,.3)',
              borderRadius:18, padding:20, textAlign:'center',
            }}>
              <div style={{ fontSize:28, marginBottom:8 }}>🤝</div>
              <h3 style={{ fontFamily:fonts.display, fontSize:17, fontWeight:700, color:'white', margin:'0 0 8px' }}>Unlock Premium</h3>
              <p style={{ fontSize:12, color:'rgba(255,255,255,.55)', lineHeight:1.6, marginBottom:12 }}>
                Unlimited questions, lab result interpretation, and profile-aware answers.
              </p>
              <div style={{ display:'flex', gap:8, marginBottom:14 }}>
                <div style={{ flex:1, padding:'10px 6px', borderRadius:10, background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.1)', textAlign:'center' }}>
                  <span style={{ fontFamily:fonts.display, fontSize:20, fontWeight:800, color:'white', display:'block' }}>$19</span>
                  <span style={{ fontSize:10, color:'rgba(255,255,255,.4)', textTransform:'uppercase', letterSpacing:'.06em' }}>/month</span>
                </div>
                <div style={{ flex:1, padding:'10px 6px', borderRadius:10, background:'rgba(102,126,234,.2)', border:'1px solid rgba(102,126,234,.4)', textAlign:'center', position:'relative' }}>
                  <div style={{ position:'absolute', top:-9, left:'50%', transform:'translateX(-50%)', background:'linear-gradient(135deg,#1A5EA8,#0B2545)', color:'white', fontSize:8, fontWeight:700, padding:'2px 8px', borderRadius:10, whiteSpace:'nowrap', letterSpacing:'.06em' }}>BEST VALUE</div>
                  <span style={{ fontFamily:fonts.display, fontSize:20, fontWeight:800, color:'white', display:'block' }}>$99</span>
                  <span style={{ fontSize:10, color:'rgba(255,255,255,.4)', textTransform:'uppercase', letterSpacing:'.06em' }}>/year</span>
                </div>
              </div>
              {['✅ Unlimited questions','✅ Lab result upload','✅ PDF & image support','✅ Profile-aware answers','✅ 7-day free trial'].map((f,i) => (
                <div key={i} style={{ fontSize:12, color:'rgba(255,255,255,.65)', textAlign:'left', marginBottom:5 }}>{f}</div>
              ))}
              <button style={{
                display:'block', width:'100%', padding:11, marginTop:14,
                background:'linear-gradient(135deg,#1A5EA8,#0B2545)',
                color:'white', border:'none', borderRadius:9,
                fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:fonts.body,
                boxShadow:'0 4px 16px rgba(102,126,234,.4)',
              }}>
                Start 7-Day Free Trial →
              </button>
              <p style={{ fontSize:10, color:'rgba(255,255,255,.25)', marginTop:8 }}>No credit card required</p>
            </div>
          )}

          {/* Focus guide */}
          <div style={{
            background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)',
            borderRadius:16, padding:'18px 16px',
          }}>
            <h4 style={{ fontFamily:fonts.display, fontSize:15, fontWeight:700, color:'white', margin:'0 0 14px' }}>Focus Areas</h4>
            {CONTEXTS.map((ctx, i) => (
              <button
                key={ctx.id}
                onClick={() => handleContextSelect(ctx)}
                style={{
                  display:'flex', alignItems:'center', gap:10, width:'100%',
                  padding:'8px 10px', borderRadius:9, border:'none', cursor:'pointer',
                  background: activeContext.id === ctx.id ? `${ctx.color}18` : 'transparent',
                  marginBottom: i < CONTEXTS.length - 1 ? 4 : 0,
                  transition:'all .2s',
                }}
              >
                <span style={{ fontSize:16, flexShrink:0 }}>{ctx.icon}</span>
                <span style={{
                  fontFamily:fonts.body, fontSize:13,
                  fontWeight: activeContext.id === ctx.id ? 600 : 400,
                  color: activeContext.id === ctx.id ? ctx.color : 'rgba(255,255,255,.6)',
                  textAlign:'left',
                }}>
                  {ctx.tooltip}
                </span>
                {activeContext.id === ctx.id && (
                  <div style={{ marginLeft:'auto', width:6, height:6, borderRadius:'50%', background:ctx.color, flexShrink:0 }}/>
                )}
              </button>
            ))}
          </div>

        </div>
      </div>

      {/* AI Data Disclosure Modal — shown before first message ever sent */}
      {showAIDisclosure && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.7)', backdropFilter:'blur(8px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1001, padding:24 }}>
          <div style={{ background:`linear-gradient(135deg,${C.dark},${C.navy})`, borderRadius:24, border:'1px solid rgba(255,255,255,.15)', padding:40, maxWidth:480, width:'100%', boxShadow:'0 40px 80px rgba(0,0,0,.5)' }}>
            <div style={{ fontSize:36, marginBottom:16, textAlign:'center' }}>🤖</div>
            <h3 style={{ fontFamily:fonts.display, fontSize:22, fontWeight:600, color:'white', textAlign:'center', marginBottom:16 }}>Before You Chat with Care Copilot</h3>
            <p style={{ fontSize:14, color:'rgba(255,255,255,.7)', lineHeight:1.7, marginBottom:16 }}>
              Care Copilot uses <strong>Claude</strong>, an AI service by Anthropic, delivered securely through <strong>AWS Bedrock</strong> under a signed Business Associate Agreement (BAA).
            </p>
            <p style={{ fontSize:14, color:'rgba(255,255,255,.7)', lineHeight:1.7, marginBottom:16 }}>
              When you use Care Copilot, your messages and any health information you enter in the conversation are sent to this AI service to generate a response.
            </p>
            <p style={{ fontSize:13, color:'rgba(255,255,255,.55)', lineHeight:1.7, marginBottom:24 }}>
              This is also described in our{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color:'#a5b4fc' }}>Privacy Policy</a>
              . You can decline and continue using the rest of NephroTrax without Care Copilot.
            </p>
            <div style={{ display:'flex', gap:12 }}>
              <button
                onClick={() => { setShowAIDisclosure(false); setPendingQuestion(null); }}
                style={{ flex:1, padding:12, background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.2)', borderRadius:10, color:'white', fontSize:14, cursor:'pointer', fontFamily:fonts.body }}
              >
                No thanks
              </button>
              <button
                onClick={() => {
                  recordAIDisclosureAcceptance();
                  setShowAIDisclosure(false);
                  const question = pendingQuestion;
                  setPendingQuestion(null);
                  if (question) handleSend(question);
                }}
                style={{ flex:1, padding:12, background:'linear-gradient(135deg,#1A5EA8,#0B2545)', border:'none', borderRadius:10, color:'white', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:fonts.body }}
              >
                I Understand, Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Consent Modal */}
      {showConsent && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.7)', backdropFilter:'blur(8px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:24 }}>
          <div style={{ background:`linear-gradient(135deg,${C.dark},${C.navy})`, borderRadius:24, border:'1px solid rgba(255,255,255,.15)', padding:40, maxWidth:480, width:'100%', boxShadow:'0 40px 80px rgba(0,0,0,.5)' }}>
            <div style={{ fontSize:36, marginBottom:16, textAlign:'center' }}>🔒</div>
            <h3 style={{ fontFamily:fonts.display, fontSize:22, fontWeight:600, color:'white', textAlign:'center', marginBottom:16 }}>Lab Result Storage Consent</h3>
            <p style={{ fontSize:14, color:'rgba(255,255,255,.7)', lineHeight:1.7, marginBottom:16 }}>
              May we securely store your uploaded lab results to help improve care for CKD patients?
            </p>
            <ul style={{ fontSize:13, color:'rgba(255,255,255,.6)', lineHeight:1.8, paddingLeft:20, marginBottom:24 }}>
              <li>Your data is encrypted and stored securely on AWS</li>
              <li>It may be used in de-identified, aggregated research</li>
              <li>Your personal information is never sold or shared</li>
              <li>You can withdraw consent at any time</li>
            </ul>
            <div style={{ display:'flex', gap:12 }}>
              <button onClick={() => { setConsentGiven(false); setShowConsent(false); if (uploadedFile) processFileUpload(uploadedFile); }} style={{ flex:1, padding:12, background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.2)', borderRadius:10, color:'white', fontSize:14, cursor:'pointer', fontFamily:fonts.body }}>
                No thanks
              </button>
              <button onClick={() => { setConsentGiven(true); setShowConsent(false); if (uploadedFile) processFileUpload(uploadedFile); }} style={{ flex:1, padding:12, background:'linear-gradient(135deg,#1A5EA8,#0B2545)', border:'none', borderRadius:10, color:'white', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:fonts.body }}>
                Yes, I consent
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CareCopilot;